import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import { v4 } from 'uuid'

import { SectionFieldModel, SelectFieldModel } from '@types'

import AddIcon from '@mui/icons-material/AddRounded'
import trashIcon from '@assets/images/trash-icon.svg'

import { Input } from '@components/Input'
import { Select, Option } from '@components/Select'

import { FormFieldCard, FormFieldCardProps } from '../FormFieldCard'

import { PopUpImportResponses } from '../../../PopUpImportResponses'

import {
  AddOptionButton,
  ButtonContainer,
  Container,
  ImportResponsesButton,
  InputsContainer,
  Label,
  Row,
  RowTitle,
  SectionContainer,
  SectionsContainer,
  Selected,
} from './FormFieldSelect.styled'

type ActionsProps = Pick<
  FormFieldCardProps,
  'canDown' | 'canUp' | 'onDelete' | 'onDuplicate' | 'onUp' | 'onDown' | 'hideRequired'
>

type FormFieldSelectContentProps = {
  field: SelectFieldModel
  sections: SectionFieldModel[]
  projectId: string
  onChange: (value: Partial<SelectFieldModel>) => void
  onDeleteOption: (fieldId: string, optionId: string) => void
}

type FormFieldSelectProps = FormFieldSelectContentProps & ActionsProps

export const FormFieldSelect: React.FC<FormFieldSelectProps> = ({
  field,
  sections,
  projectId,
  onDeleteOption,
  onChange,
  ...actionsProps
}: FormFieldSelectProps) => {
  return (
    <FormFieldCard
      name={field.label}
      required={field.required}
      onChangeRequired={(value) => {
        onChange({ required: value })
      }}
      {...actionsProps}
    >
      <FormFieldSelectContent
        field={field}
        projectId={projectId}
        onChange={onChange}
        onDeleteOption={onDeleteOption}
        sections={sections}
      />
    </FormFieldCard>
  )
}

export const FormFieldSelectContent: React.FC<FormFieldSelectContentProps> = ({
  field,
  projectId,
  sections,
  onChange,
  onDeleteOption,
}: FormFieldSelectContentProps) => {
  const { t } = useTranslation()

  const [importing, setImporting] = useState<boolean>(false)

  const sectionsOptions = [
    {
      id: '',
      label: '',
      data: null,
      Component: () => (
        <Selected>{t('modules_project-form-models_components_FormFieldSelect_remove_section')}</Selected>
      ),
    },
    ...sections.map((section) => ({
      id: section.id,
      label: section.label,
      data: section,
      Component: () => <Selected>{section.label}</Selected>,
    })),
  ]

  const getSelectedSection = (id: string) => sectionsOptions.find((section) => section.id === id)

  const handleSectionSelect = (index: number, section: Option | undefined) => {
    const newOptions = [...field.options]
    newOptions[index] = {
      ...newOptions[index],
      redirectTo: section?.id === '' ? undefined : section?.id,
    }
    onChange({ options: newOptions })
  }

  return (
    <>
      <Container>
        <Input
          type="text"
          required
          label={t('modules_project-form-models_components_FormFieldSelect_question_label')}
          value={field.label}
          onChange={(value) => onChange({ label: value })}
        />
        <Row>
          <InputsContainer>
            <RowTitle>
              <Label>{t('modules_project-form-models_components_FormFieldSelect_options_label')}</Label>
              <ImportResponsesButton type="button" onClick={() => setImporting(true)}>
                <span>{t('modules_project-form-models_components_FormFieldSelect_import_responses')}</span>
              </ImportResponsesButton>
            </RowTitle>

            {field.options.map((option, index) => (
              <Input
                key={index}
                type="text"
                required
                placeholder={`Option ${index + 1}`}
                value={option.label}
                onChange={(label) => {
                  const newOptions = [...field.options]
                  newOptions[index] = { ...option, label }
                  onChange({ options: newOptions })
                }}
              />
            ))}
          </InputsContainer>

          <SectionsContainer>
            <Label>{t('modules_project-form-models_components_FormFieldSelect_sections_label')}</Label>

            {field.options.map((option, index) => (
              <SectionContainer key={index}>
                <Select
                  placeholder={t('modules_project-form-models_components_FormFieldSelect_section_placeholder')}
                  selected={option.redirectTo ? getSelectedSection(option.redirectTo) : undefined}
                  options={sectionsOptions}
                  onClick={(section) => handleSectionSelect(index, section)}
                />
                {field.options.length > 2 && (
                  <ButtonContainer
                    onClick={(e) => {
                      e.preventDefault()
                      e.stopPropagation()

                      onDeleteOption(field.id, option.id)
                    }}
                  >
                    <img src={trashIcon} />
                  </ButtonContainer>
                )}
              </SectionContainer>
            ))}
          </SectionsContainer>
        </Row>
        <AddOptionButton
          type="button"
          onClick={() => {
            const newOptions = [...field.options]
            newOptions.push({ id: v4(), label: '' })
            onChange({ options: newOptions })
          }}
        >
          <AddIcon />
          <span>{t('modules_project-form-models_components_FormFieldSelect_add_option')}</span>
        </AddOptionButton>
      </Container>

      {importing && (
        <PopUpImportResponses
          projectId={projectId}
          onSubmit={(responses) => {
            const newOptions = responses.map((response) => ({ id: v4(), label: response }))
            onChange({ options: newOptions })
            setImporting(false)
          }}
          onClose={() => {
            setImporting(false)
          }}
        />
      )}
    </>
  )
}
